import React from 'react';
import { Dimensions } from 'react-native';
import {
  VictoryGroup,
  VictoryBar,
  VictoryChart,
  VictoryLine,
  VictoryLabel,
  VictoryTheme,
  VictoryAxis,
} from 'victory';
import * as GlobalVariableContext from '../config/GlobalVariableContext';
import Breakpoints from '../utils/Breakpoints';

const setNewFontSize = width => {
  if (width >= Breakpoints.BigScreen) {
    return 14;
  } else if (width >= Breakpoints.Desktop) {
    return 12;
  } else if (width >= Breakpoints.Laptop) {
    return 8;
  } else {
    return 8;
  }
};

const setNewBarSize = width => {
  if (width >= Breakpoints.BigScreen) {
    return 35;
  } else if (width >= Breakpoints.Desktop) {
    return 20;
  } else if (width >= Breakpoints.Laptop) {
    return 15;
  } else {
    return 15;
  }
};

const setNewPadding = width => {
  if (width >= Breakpoints.BigScreen) {
    return 55;
  } else if (width >= Breakpoints.Desktop) {
    return 45;
  } else if (width >= Breakpoints.Laptop) {
    return 35;
  } else {
    return 35;
  }
};

const convertToLabelledData = array => {
  return array.map((value, index) => ({
    month: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ][index],
    value: value,
    label: value ? value.toString() : 0,
  }));
};

const generateMonthLabels = startingDate => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const startDate = new Date(startingDate);
  const startYear = startDate.getUTCFullYear();
  const startMonth = startDate.getUTCMonth();
  const labels = [];

  for (let i = 0; i < 12; i++) {
    const monthIndex = (startMonth + i) % 12;
    const yearOffset = Math.floor((startMonth + i) / 12);
    const monthYear = `${months[monthIndex]}, ${startYear + yearOffset}`;
    labels.push(monthYear);
  }

  return labels;
};

const getDynamicTickValues = array => {
  const slicedArray = array.slice(0, 12);
  const maxValue = Math.max(...slicedArray);
  const defaultTickValues = [
    0, 100000, 200000, 300000, 400000, 500000, 600000, 700000,
  ];

  if (maxValue <= 700000) {
    return defaultTickValues;
  }

  const dynamicTickValues = [];
  const step = 100000;

  for (let i = 0; i <= Math.ceil(maxValue / step); i++) {
    dynamicTickValues.push(i * step);
  }

  return dynamicTickValues;
};

export const App = ({ cashOnHand, totalCashPaid }) => {
  const variables = GlobalVariableContext.useValues();
  const labelledCashData = convertToLabelledData(
    Array.isArray(cashOnHand.ending) ? cashOnHand.ending.slice(0, 12) : []
  );
  const labelledTotalPaidData = convertToLabelledData(
    totalCashPaid.slice(0, 12)
  );
  const monthLabels = generateMonthLabels(variables.STARTING_DATE);
  const dynamicTickValues = getDynamicTickValues(
    Array.isArray(cashOnHand.ending) ? cashOnHand.ending.slice(0, 12) : []
  );

  return (
    <VictoryChart
      width={Dimensions.get('window').width * 0.75}
      domainPadding={setNewPadding(Dimensions.get('window').width)}
      theme={VictoryTheme.material}
    >
      <VictoryAxis
        tickValues={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
        tickFormat={monthLabels}
        style={{
          axis: { stroke: 'white', strokeWidth: 2 },
          ticks: { stroke: 'transparent' },
          tickLabels: { fill: 'white', fontSize: 8 },
          grid: { stroke: 'transparent' },
        }}
      />
      <VictoryAxis
        dependentAxis
        tickValues={dynamicTickValues}
        tickFormat={t =>
          `$${new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
          }).format(t)}`
        }
        style={{
          axis: { stroke: 'transparent' },
          ticks: { stroke: 'transparent' },
          tickLabels: { fill: 'white', fontSize: 8, padding: 0 },
          grid: { stroke: '#62DAF3', strokeWidth: 0.5, strokeDasharray: 2 },
        }}
        fixLabelOverlap={true}
      />
      <VictoryGroup>
        <VictoryBar
          data={labelledTotalPaidData}
          x="month"
          y="value"
          alignment="middle"
          barWidth={setNewBarSize(Dimensions.get('window').width)}
          style={{
            labels: {
              fill: 'black',
              fontSize: setNewFontSize(Dimensions.get('window').width),
            },
            data: { fill: '#00B0F0' },
          }}
          labelComponent={
            <VictoryLabel
              dy={0}
              textAnchor="middle"
              verticalAnchor="middle"
              backgroundStyle={{
                fill: 'white',
                stroke: '#8FD400',
                strokeWidth: 1,
              }}
              backgroundPadding={3}
              style={[
                {
                  fill: 'black',
                  fontSize: setNewFontSize(Dimensions.get('window').width),
                  fontFamily: 'Inter_500Medium',
                },
              ]}
              text={({ datum }) => {
                const formattedValue = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                }).format(datum.value);
                return [formattedValue];
              }}
            />
          }
        />
        <VictoryLine
          data={labelledCashData}
          x="month"
          y="value"
          style={{
            labels: {
              fill: 'black',
              fontSize: setNewFontSize(Dimensions.get('window').width),
            },
            data: { stroke: '#8FD400' },
          }}
          labelComponent={
            <VictoryLabel
              dy={0}
              backgroundStyle={{
                fill: '#8FD400',
                stroke: 'white',
                strokeWidth: 1,
              }}
              backgroundPadding={3}
              verticalAnchor="middle"
              textAnchor="middle"
              style={[
                {
                  fill: 'black',
                  fontSize: setNewFontSize(Dimensions.get('window').width),
                  fontFamily: 'Inter_500Medium',
                },
              ]}
              text={({ datum }) => {
                const formattedValue = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                }).format(datum.value);
                return [formattedValue];
              }}
            />
          }
        />
      </VictoryGroup>
    </VictoryChart>
  );
};
